import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/base/SEOHead';
import { localBusinessSchema, buildBreadcrumbSchema, buildFaqSchema, buildServiceSchema, buildGraphSchema } from '@/components/base/schemas';
import { Link } from 'react-router-dom';
import { useState } from 'react';

const serviceSchema = buildServiceSchema(
  'Réparation de fenêtres PVC à Paris 15ᵉ',
  'Spécialiste réparation fenêtre PVC dans le 15ᵉ : quincaillerie, crémone, joints, réglage.'
);

const breadcrumbSchema = buildBreadcrumbSchema([
  { name: 'Accueil', item: 'https://fenetre-paris15.fr/' },
  { name: 'Fenêtres PVC', item: 'https://fenetre-paris15.fr/reparation-fenetre-pvc-paris-15/' },
]);

const faqs = [
  { q: 'Pourquoi ma poignée de fenêtre PVC tourne-t-elle dans le vide ?', a: 'C\'est un problème très fréquent. La cause la plus probable est un carré de poignée cassé ou une crémone désengagée. Le carré est la tige métallique qui relie la poignée au mécanisme. Avec l\'usure, il peut se casser net. La crémone, elle, peut se déboîter de son logement. Dans les deux cas, DF PRO diagnostique et remplace la pièce défectueuse rapidement.' },
  { q: 'Peut-on réparer une fenêtre PVC plutôt que la remplacer ?', a: 'Dans la grande majorité des cas, oui. Les fenêtres PVC sont conçues pour durer plusieurs décennies. Ce qui s\'use, c\'est la quincaillerie (poignées, crémones, joints, galets). Remplacer une fenêtre entière pour une poignée cassée est un non-sens économique et écologique. DF PRO privilégie toujours la réparation quand elle est possible.' },
  { q: 'Intervenez-vous sur les fenêtres PVC de toutes marques ?', a: 'Absolument. Nous maîtrisons la quincaillerie de tous les grands fabricants : Ferco, Roto, Maco, Winkhaus, Siegenia, GU, Hautau, etc. Nous disposons d\'un stock de pièces détachées couvrant l\'essentiel des références du marché.' },
];

export default function PvcPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <Layout>
      <SEOHead
        title="Réparation Fenêtre PVC Paris 15 (75015) — Devis Gratuit | DF PRO"
        description="Spécialiste réparation fenêtre PVC dans le 15ᵉ : quincaillerie, crémone, joints, réglage. Intervention 24/48h, devis gratuit. ☎ 01 87 66 81 55"
        canonicalUrl="https://fenetre-paris15.fr/reparation-fenetre-pvc-paris-15/"
        schema={buildGraphSchema(localBusinessSchema, breadcrumbSchema, buildFaqSchema(faqs), serviceSchema)}
      />

      {/* Breadcrumb */}
      <div className="bg-brand-mint/30 py-3">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav aria-label="Fil d'Ariane" className="text-xs text-brand-text/60">
            <Link to="/" className="hover:text-brand-accent transition-colors">Accueil</Link>
            <span className="mx-2">/</span>
            <span className="text-brand-accent font-medium">Fenêtres PVC</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
            <div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-brand-dark leading-tight mb-4">Réparation de fenêtres PVC à Paris 15ᵉ</h1>
              <p className="text-brand-text/70 text-base leading-relaxed mb-6">
                Votre fenêtre PVC ferme mal, la poignée tourne dans le vide ou le joint est durci ? DF PRO est le spécialiste de la <strong>réparation de quincaillerie PVC</strong> dans le 15ᵉ arrondissement. Nous diagnostiquons et réparons votre fenêtre sans remplacer l'ouvrant.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/devis-fenetre-paris-15/" className="btn-primary text-sm">Devis gratuit</Link>
                <a href="tel:+33187668155" className="btn-outline text-sm"><i className="ri-phone-line"></i> 01 87 66 81 55</a>
              </div>
            </div>
            <div className="aspect-[4/3] rounded-2xl overflow-hidden">
              <img src="/images/dfpro-pvc-hero.webp" alt="Fenêtre PVC dans intérieur parisien — DF PRO réparation fenêtre PVC Paris 15" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Pannes fréquentes */}
      <section className="py-12 md:py-20 bg-brand-mint/40" aria-labelledby="pannes-title">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 id="pannes-title" className="section-title mb-8">Pannes fréquentes des fenêtres PVC</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {[
              { title: 'Fermeture difficile', desc: 'Le vantail frotte contre le dormant, la fenêtre ne se ferme plus complètement. Souvent un problème de réglage des paumelles ou de galets de guidage usés. Un simple ajustement peut tout remettre d\'équerre.' },
              { title: 'Poignée qui tourne dans le vide', desc: 'Le carré de poignée est cassé ou la crémone est désengagée. La poignée tourne sans actionner le mécanisme de verrouillage. Pièce à remplacer, intervention rapide.' },
              { title: 'Crémone déréglée', desc: 'Les galets de crémone ne s\'alignent plus avec les gâches. Résultat : la fenêtre ne verrouille pas correctement, courants d\'air et insécurité. Réglage de précision nécessaire.' },
              { title: 'Joint durci ou écrasé', desc: 'Avec le temps, les joints EPDM perdent leur élasticité. Infiltrations d\'air, buée sur le vitrage, perte d\'isolation thermique et acoustique. Remplacement des joints périphériques.' },
              { title: 'Condensation entre les vitres', desc: 'Le double vitrage est embué de l\'intérieur, signe que le joint d\'étanchéité périphérique du vitrage isolant a cédé. Nous pouvons diagnostiquer et orienter, mais le remplacement du vitrage est hors périmètre.' },
              { title: 'Vantail qui frotte en partie basse', desc: 'Affaissement progressif du vantail dû au poids ou à l\'usure des paumelles. Un réglage des compas et des paumelles suffit généralement. Pas besoin de changer la fenêtre.' },
            ].map((item, idx) => (
              <div key={idx} className="bg-white rounded-xl p-5 md:p-6 card-hover">
                <h3 className="font-heading font-bold text-brand-dark text-base mb-2">{item.title}</h3>
                <p className="text-brand-text/60 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ce qu'on répare */}
      <section className="py-12 md:py-20 bg-white" aria-labelledby="quoi-title">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden order-2 lg:order-1">
              <img src="/images/dfpro-pvc-repair.webp" alt="Réparation quincaillerie fenêtre PVC par technicien DF PRO Paris 15" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="order-1 lg:order-2">
              <h2 id="quoi-title" className="section-title mb-4">Ce que nous réparons sur vos fenêtres PVC</h2>
              <p className="text-brand-text/70 leading-relaxed mb-4">
                DF PRO intervient exclusivement sur la <strong>quincaillerie et les mécanismes</strong> de vos fenêtres PVC. Notre périmètre d'intervention couvre :
              </p>
              <ul className="space-y-2 text-brand-text/70 text-sm">
                <li className="flex items-start gap-2"><i className="ri-check-line text-brand-accent mt-0.5"></i> Poignées de fenêtre (remplacement, adaptation)</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-brand-accent mt-0.5"></i> Crémones et tringleries (réglage, remplacement)</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-brand-accent mt-0.5"></i> Gâches et contre-gâches</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-brand-accent mt-0.5"></i> Galets de guidage et de verrouillage</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-brand-accent mt-0.5"></i> Paumelles et compas (réglage, graissage, remplacement)</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-brand-accent mt-0.5"></i> Joints d'étanchéité périphériques</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-brand-accent mt-0.5"></i> Réglage général de l'ouvrant (équerrage, pression)</li>
              </ul>
              <p className="text-brand-text/50 text-xs italic mt-4">Hors périmètre : remplacement de vitrage et traitement/peinture.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Réparer vs Remplacer + Tarifs */}
      <section className="py-12 md:py-20 bg-brand-mint/40">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
            <div className="bg-white rounded-xl p-6 md:p-8">
              <h2 className="font-heading font-bold text-brand-dark text-xl mb-4">Réparer plutôt que remplacer</h2>
              <p className="text-brand-text/70 text-sm leading-relaxed mb-4">
                Une fenêtre PVC a une durée de vie de 30 à 40 ans. La remplacer pour une simple panne de quincaillerie est un gaspillage financier et écologique. Une réparation bien faite redonne à votre fenêtre toutes ses performances d'origine pour une fraction du prix d'un remplacement.
              </p>
              <p className="text-brand-text/70 text-sm leading-relaxed">
                Nous sommes des <strong>réparateurs, pas des vendeurs de fenêtres</strong>. Si votre fenêtre peut être réparée, nous vous le dirons. Si elle doit être remplacée, nous vous le dirons aussi, en toute transparence.
              </p>
            </div>
            <div className="bg-gradient-to-br from-brand-gradientFrom to-brand-gradientTo rounded-xl p-6 md:p-8 text-white">
              <h2 className="font-heading font-bold text-xl mb-4">Tarifs et devis</h2>
              <p className="text-white/80 text-sm leading-relaxed mb-4">
                Chaque panne est unique. C'est pourquoi nous établissons un <strong>devis gratuit après diagnostic</strong> sur place. Voici des ordres de prix indicatifs :
              </p>
              <ul className="space-y-2 text-white/80 text-sm mb-6">
                <li className="flex justify-between"><span>Remplacement poignée</span><span className="font-semibold text-white">80 – 150 €</span></li>
                <li className="flex justify-between"><span>Remplacement crémone</span><span className="font-semibold text-white">120 – 250 €</span></li>
                <li className="flex justify-between"><span>Réglage complet ouvrant</span><span className="font-semibold text-white">90 – 180 €</span></li>
                <li className="flex justify-between"><span>Remplacement joints</span><span className="font-semibold text-white">100 – 200 €</span></li>
              </ul>
              <Link to="/devis-fenetre-paris-15/" className="inline-flex items-center gap-2 bg-white text-brand-dark font-semibold rounded-full px-6 py-3 text-sm hover:opacity-90 transition-opacity whitespace-nowrap">
                <i className="ri-file-text-line"></i> Demander un devis précis
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h2 className="section-title mb-8 text-center">Questions fréquentes — Fenêtres PVC</h2>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={idx} className="border border-gray-200 rounded-xl overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-brand-mint/30 transition-colors" aria-expanded={openFaq === idx}>
                  <span className="font-heading font-bold text-brand-dark text-sm pr-4">{faq.q}</span>
                  <i className={`ri-${openFaq === idx ? 'subtract' : 'add'}-line text-brand-accent text-lg flex-shrink-0`}></i>
                </button>
                {openFaq === idx && (
                  <div className="px-5 pb-4"><p className="text-brand-text/70 text-sm leading-relaxed">{faq.a}</p></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Liens connexes + CTA */}
      <section className="py-12 md:py-16 bg-brand-mint/40">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h2 className="section-title mb-4">Besoin d'un diagnostic ?</h2>
          <p className="section-subtitle mx-auto mb-6">Contactez DF PRO pour une intervention rapide sur votre fenêtre PVC dans le 15ᵉ</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-10">
            <Link to="/devis-fenetre-paris-15/" className="btn-primary text-sm">Devis gratuit</Link>
            <a href="tel:+33187668155" className="btn-outline text-sm"><i className="ri-phone-line"></i> 01 87 66 81 55</a>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
            <Link to="/blog/reparer-poignee-fenetre-qui-tourne-dans-le-vide/" className="text-brand-accent hover:underline">Article : poignée qui tourne dans le vide</Link>
            <span className="text-brand-text/30">|</span>
            <Link to="/zones-intervention-paris-15/" className="text-brand-accent hover:underline">Zones d'intervention dans le 15ᵉ</Link>
            <span className="text-brand-text/30">|</span>
            <Link to="/reparation-fenetre-aluminium-paris-15/" className="text-brand-accent hover:underline">Fenêtres Aluminium</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}